/**
 * Firebase Configuration for HypeDirector Landing Page
 * Supports multiple environments (local, nonprod, prod)
 */

import { infoLog } from '../utils/logger';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

const configs: Record<string, FirebaseConfig> = {
  local: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
    authDomain: 'hypedirector-nonprod-cf998.firebaseapp.com',
    projectId: 'hypedirector-nonprod-cf998',
    storageBucket: 'hypedirector-nonprod-cf998.appspot.com',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  },
  nonprod: {
    apiKey: 'AIzaSyDw9W4sczbkVC1JBVEGilWw5K1YibQdkAg',
    authDomain: 'hypedirector-nonprod-cf998.firebaseapp.com',
    projectId: 'hypedirector-nonprod-cf998',
    storageBucket: 'hypedirector-nonprod-cf998.firebasestorage.app',
    messagingSenderId: '300340791121',
    appId: '1:300340791121:web:557cff65be587bd6563570',
  },
  prod: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: 'hypedirector-prod-b77cf.firebaseapp.com',
    projectId: 'hypedirector-prod-b77cf',
    storageBucket: 'hypedirector-prod-b77cf.appspot.com',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  },
};

/**
 * Detect environment from hostname
 */
function detectEnvironment(): string {
  // For SSR or non-browser environments
  if (typeof window === 'undefined') {
    return import.meta.env.VITE_ENVIRONMENT || 'local';
  }

  const hostname = window.location.hostname;
  
  // Production domains
  if (hostname.includes('hypedirector.com') && !hostname.includes('dev')) {
    return 'prod';
  }
  
  // Nonprod domains (dev.* or *nonprod*)
  if (hostname.includes('dev') || hostname.includes('nonprod')) {
    return 'nonprod';
  }
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  // Default fallback
  return import.meta.env.VITE_ENVIRONMENT || 'local';
}

/**
 * Get the correct authDomain based on current hostname
 * This is critical for signInWithRedirect to work properly
 */
function getAuthDomain(): string {
  if (typeof window === 'undefined') {
    return 'hypedirector-nonprod-cf998.firebaseapp.com';
  }

  const hostname = window.location.hostname;
  
  // IMPORTANT: Always use the current hostname as authDomain
  // This prevents cross-domain auth state loss
  // Firebase will handle the redirect properly as long as the domain
  // is in the authorized domains list
  return hostname;
}

export function getFirebaseConfig(): FirebaseConfig {
  const env = detectEnvironment();
  const config = configs[env];
  
  if (!config) {
    console.warn(`Firebase config not found for environment: ${env}, falling back to local`);
    return configs.local;
  }
  
  // Override authDomain to match current hostname for proper redirect handling
  const authDomain = getAuthDomain();
  const finalConfig = { ...config, authDomain };
  
  infoLog(`[Firebase Config] Using environment: ${env}`);
  infoLog(`[Firebase Config] Auth domain: ${authDomain}`);
  return finalConfig;
}

